// The board's columns, and how wide each one is.
//
// WHY THE WIDTHS ARE DATA NOW
//
// The template was one hard-coded grid-cols string with the task name at
// minmax(0,3fr): eleven columns squeezed onto a 78rem board, the name given
// whatever was left, and everything past that truncated. On a laptop with the
// detail panel open that is about forty characters, and the titles on this board
// that matter most are the longest ones, because they carry the car and the
// person inside the text: "Submit insurer claim file for Bokamoso's LY80CBGP"
// read as "Submit insurer claim file for Bokamo...". The information you needed
// in order to know whether the row was yours was the part that got cut.
//
// So each column now carries its own width, dragged from the header, and kept
// per person in this browser. Deliberately NOT in Postgres: a column width is a
// preference about one screen on one machine, it is not a fact about the
// business, and putting it in the database would mean a migration every time
// somebody drags a divider.

export type ColumnKey =
  | 'task'
  | 'owner'
  | 'status'
  | 'priority'
  | 'authority'
  | 'links'
  | 'driver'
  | 'start'
  | 'target'
  | 'done'
  | 'ontime'
  | 'files'
  | 'talk'
  | 'created'
  | 'updated'

export type BoardColumn = {
  key: ColumnKey
  label: string
  /** Header alignment. The cells themselves keep their own alignment. */
  align?: 'left' | 'center'
  /** Width in px with nothing stored, on a screen wide enough for it. */
  wide: number
  /** Width in px with nothing stored, below 640px. See readColumnWidths. */
  narrow: number
  /** Below this the column stops being readable, so the drag stops too. */
  min: number
}

// The wide defaults are the old grid template converted to pixels, so anybody
// who never touches a divider sees very nearly the board they saw yesterday.
// The one deliberate change is the task name, which was flexible and is now a
// real 340px, because "flexible" in practice meant "first to be squeezed".
export const BOARD_COLUMNS: BoardColumn[] = [
  { key: 'task', label: 'Task', wide: 340, narrow: 240, min: 160 },
  { key: 'owner', label: 'Owner', wide: 112, narrow: 96, min: 72 },
  { key: 'status', label: 'Status', align: 'center', wide: 120, narrow: 112, min: 96 },
  { key: 'priority', label: 'Priority', align: 'center', wide: 96, narrow: 92, min: 80 },
  // Added 27 August 2026 with the delegation work. Beside Priority rather than
  // out past the fold, because it answers a question the person reading the row
  // needs answered before they start: may I just do this. A column somebody has
  // to scroll to find is a column that stays empty, and this one is empty on all
  // 359 rows today.
  { key: 'authority', label: 'Authority', align: 'center', wide: 116, narrow: 104, min: 88 },
  { key: 'links', label: 'Vehicle / owner', wide: 144, narrow: 128, min: 96 },
  { key: 'driver', label: 'Driver', wide: 128, narrow: 112, min: 96 },
  { key: 'start', label: 'Start', wide: 76, narrow: 76, min: 64 },
  { key: 'target', label: 'Target', wide: 76, narrow: 76, min: 64 },
  { key: 'done', label: 'Done', wide: 76, narrow: 76, min: 64 },
  { key: 'ontime', label: 'On time', wide: 88, narrow: 88, min: 72 },
  // Added with attachments, 26 August 2026. Narrow on purpose: it carries an
  // icon and at most two digits, and it earns its place only on the rows that
  // have a file. Anybody who never attaches anything sees an empty column, which
  // is the cost of being able to see at a glance which tasks carry evidence.
  { key: 'files', label: 'Files', align: 'center', wide: 48, narrow: 48, min: 44 },
  { key: 'talk', label: 'Talk', align: 'center', wide: 48, narrow: 48, min: 44 },
  // Added 26 August 2026. Both are system facts and neither is editable: a
  // created date you can type is not a created date.
  //
  // They sit LAST rather than beside Start and Target, and that is deliberate.
  // The dated columns in the middle are the ones somebody promised and is judged
  // on; these two are provenance, read when you are asking "how long has this
  // been sitting here" or "did anyone touch it since the meeting". Putting them
  // next to Target would invite reading a created date as a commitment. Anybody
  // who wants them earlier drags them wider and scrolls; anybody who does not
  // care never sees them, because the board scrolls horizontally and these are
  // past the fold on a laptop.
  { key: 'created', label: 'Created', wide: 84, narrow: 76, min: 64 },
  { key: 'updated', label: 'Updated', wide: 84, narrow: 76, min: 64 },
]

export type ColumnWidths = Record<ColumnKey, number>

// A drag cannot produce a column wider than this. Not a style rule: a stored
// 6000px width, from a mis-drag or a corrupted value, renders a board that
// cannot be scrolled back from, and the person who hits it has no way to know
// why. The reset control exists as well; this is the belt.
const MAX_WIDTH = 960

const KEY = 'fleetly.tasks.columnWidths'
const WRAP_KEY = 'fleetly.tasks.wrapNames'

/** The gap between columns, matching gap-1 in the row class. */
export const COLUMN_GAP = 4

export function defaultColumnWidths(): ColumnWidths {
  // A phone is not a small laptop. Three of the six people who use this board
  // work from one, and giving the name 340px there would push every other column
  // off the screen for somebody who is scanning statuses, not reading titles.
  // Anybody who wants the wider name on a phone drags it and it is remembered.
  let narrow = false
  try {
    narrow = typeof window !== 'undefined' && window.innerWidth < 640
  } catch {
    narrow = false
  }
  const out = {} as ColumnWidths
  for (const c of BOARD_COLUMNS) out[c.key] = narrow ? c.narrow : c.wide
  return out
}

export function clampWidth(key: ColumnKey, px: number): number {
  const col = BOARD_COLUMNS.find((c) => c.key === key)
  const min = col ? col.min : 48
  if (!Number.isFinite(px)) return col ? col.wide : min
  return Math.min(MAX_WIDTH, Math.max(min, Math.round(px)))
}

/**
 * The stored widths, defaulted and clamped column by column.
 *
 * Every value is re-validated on the way in rather than trusted. A key that is
 * no longer a column is dropped, and a column that is new since the value was
 * stored takes its default, so adding a column later does not hand somebody a
 * broken board they have to clear their browser storage to escape.
 */
export function readColumnWidths(): ColumnWidths {
  const base = defaultColumnWidths()
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return base
    const parsed = JSON.parse(raw) as Record<string, unknown>
    for (const c of BOARD_COLUMNS) {
      const v = parsed[c.key]
      if (typeof v === 'number') base[c.key] = clampWidth(c.key, v)
    }
  } catch {
    // Storage can be unavailable (private browsing, quota) or hold nonsense.
    // The defaults are a correct board, so there is nothing to report.
  }
  return base
}

export function writeColumnWidths(widths: ColumnWidths) {
  try {
    localStorage.setItem(KEY, JSON.stringify(widths))
  } catch {
    // Not worth surfacing. The widths still apply for this visit.
  }
}

export function clearColumnWidths() {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Same.
  }
}

export function readWrapNames(): boolean {
  try {
    return localStorage.getItem(WRAP_KEY) === 'on'
  } catch {
    return false
  }
}

export function writeWrapNames(on: boolean) {
  try {
    localStorage.setItem(WRAP_KEY, on ? 'on' : 'off')
  } catch {
    // Same.
  }
}

/** grid-template-columns for the header and every row, from one source. */
export function columnTemplate(widths: ColumnWidths): string {
  return BOARD_COLUMNS.map((c) => `${widths[c.key]}px`).join(' ')
}

/**
 * How wide the board is, so the scroll container knows there is something to
 * scroll to. Padding is not counted here; the caller owns its own padding.
 */
export function boardWidth(widths: ColumnWidths): number {
  const cols = BOARD_COLUMNS.reduce((n, c) => n + widths[c.key], 0)
  return cols + COLUMN_GAP * (BOARD_COLUMNS.length - 1)
}

export function widthsAreDefault(widths: ColumnWidths): boolean {
  const base = defaultColumnWidths()
  return BOARD_COLUMNS.every((c) => widths[c.key] === base[c.key])
}
